import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useJob, useCreateJob, useUpdateJob } from '@/hooks/useJobs';
import { useClients } from '@/hooks/useClients';
import { useLocations } from '@/hooks/useLocations';
import { useManagers } from '@/hooks/useManagers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AddClientModal } from './components/AddClientModal';
import { AddManagerModal } from '@/components/shared/AddManagerModal';
import { Combobox } from '@/components/ui/combobox';
import { todayLocalISO } from '@/lib/format';
import { LocationCombobox } from '@/components/shared/LocationCombobox';
import { BillingAddressPicker } from './components/BillingAddressPicker';
import { jobInputSchema } from '@jobos/shared/schemas/job';
import { useProjectTypes } from '@/hooks/useConfigurableLists';
import { useBreakdownTemplates } from '@/hooks/useBreakdownTemplates';
import type { z } from 'zod';

type JobFormInput = z.input<typeof jobInputSchema>;

// Sentinel for the "No template" picker option — shadcn SelectItem
// disallows empty-string values, so we map it to/from null.
const NO_TEMPLATE_VALUE = '__none__';
import type { Client } from '@jobos/shared/types/client';
import type { Location } from '@jobos/shared/types/location';
import type { Manager } from '@jobos/shared/types/manager';
import { formatClientName, formatManagerName } from '@jobos/shared';

export default function JobForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;

  const { data: job, isLoading: jobLoading } = useJob(id);
  const { data: projectTypes } = useProjectTypes();
  const { data: clients, isLoading: clientsLoading } = useClients();
  const { mutateAsync: createJob, isPending: isCreating } = useCreateJob();
  const { mutateAsync: updateJob, isPending: isUpdating } = useUpdateJob(id!);

  const [clientOptions, setClientOptions] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [locationOptions, setLocationOptions] = useState<Location[]>([]);
  const [managerOptions, setManagerOptions] = useState<Manager[]>([]);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const { data: locations, isLoading: locationsLoading } = useLocations(
    selectedClientId || undefined
  );
  const { data: managers, isLoading: managersLoading } = useManagers(
    selectedClientId || undefined
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm<JobFormInput>({
    resolver: zodResolver(jobInputSchema),
    defaultValues: {
      clientId: '',
      locationId: '',
      managerId: '',
      projectType: projectTypes[0] || '',
      templateId: null,
      customProjectType: '',
      description: '',
      noticeDate: todayLocalISO(),
      billingAddressLabel: '',
      billingAddressLine1: '',
      billingAddressLine2: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
    },
  });

  // Watch form values
  const clientId = watch('clientId');
  const locationId = watch('locationId');
  const managerId = watch('managerId');
  const projectType = watch('projectType');
  const templateId = watch('templateId');

  // Breakdown templates for the chosen project type
  const { data: templates = [] } = useBreakdownTemplates();
  const candidates = useMemo(
    () => templates.filter((t) => t.projectType === projectType && t.isActive),
    [templates, projectType]
  );
  const defaultTemplateId = useMemo(
    () => candidates.find((c) => c.isDefault)?.id ?? null,
    [candidates]
  );

  // Auto-set templateId when project type / candidate list changes
  useEffect(() => {
    if (candidates.length === 0) {
      setValue('templateId', null);
      return;
    }
    const current = getValues('templateId');
    const stillValid = candidates.some((c) => c.id === current);
    if (!stillValid) {
      setValue('templateId', defaultTemplateId);
    }
  }, [candidates, defaultTemplateId, setValue, getValues]);

  // Update selected client when form value changes
  useEffect(() => {
    if (clientId) {
      setSelectedClientId(clientId);
    }
  }, [clientId]);

  // Keep local client options in sync with fetched data, but don't drop newly created ones
  useEffect(() => {
    if (clients) {
      setClientOptions((prev) => {
        const mergedMap = new Map<string, Client>();
        prev.forEach((c) => mergedMap.set(c.id, c));
        clients.forEach((c) => mergedMap.set(c.id, c));
        return Array.from(mergedMap.values());
      });
    }
  }, [clients]);

  // Keep local location options in sync with fetched data, but don't drop newly created ones
  useEffect(() => {
    if (locations) {
      setLocationOptions((prev) => {
        const mergedMap = new Map<string, Location>();
        prev.forEach((l) => mergedMap.set(l.id, l));
        locations.forEach((l) => mergedMap.set(l.id, l));
        return Array.from(mergedMap.values());
      });
    }
  }, [locations]);

  // Keep local manager options in sync with fetched data, but don't drop newly created ones
  useEffect(() => {
    if (managers) {
      setManagerOptions((prev) => {
        const mergedMap = new Map<string, Manager>();
        prev.forEach((m) => mergedMap.set(m.id, m));
        managers.forEach((m) => mergedMap.set(m.id, m));
        return Array.from(mergedMap.values());
      });
    }
  }, [managers]);

  // Load job data in edit mode
  useEffect(() => {
    if (isEditMode && job) {
      setValue('clientId', job.clientId);
      setValue('locationId', job.locationId);
      setValue('managerId', job.managerId || '');
      setValue('projectType', job.projectType);
      setValue('customProjectType', job.customProjectType || '');
      setValue('description', job.description || '');
      if (job.noticeDate) setValue('noticeDate', job.noticeDate);
      setValue('billingAddressLabel', job.billingAddressLabel || '');
      setValue('billingAddressLine1', job.billingAddressLine1 || '');
      setValue('billingAddressLine2', job.billingAddressLine2 || '');
      setValue('billingCity', job.billingCity || '');
      setValue('billingState', job.billingState || '');
      setValue('billingZip', job.billingZip || '');
    }
  }, [isEditMode, job, setValue]);

  // Pre-select client from URL parameter in create mode
  useEffect(() => {
    if (!isEditMode && !clientId) {
      const clientIdFromUrl = searchParams.get('clientId');
      if (clientIdFromUrl) {
        setValue('clientId', clientIdFromUrl);
        setSelectedClientId(clientIdFromUrl);
      }
    }
  }, [isEditMode, clientId, searchParams, setValue]);

  // Auto-fill manager when location is selected
  useEffect(() => {
    if (locationId && locationOptions.length > 0) {
      const selectedLocation = locationOptions.find((l) => l.id === locationId);
      if (selectedLocation && selectedLocation.managerId) {
        // Only auto-fill if manager isn't already set or is different
        if (managerId !== selectedLocation.managerId) {
          setValue('managerId', selectedLocation.managerId);
        }
      }
    }
  }, [locationId, locationOptions, managerId, setValue]);

  const handleClientCreated = (client: Client) => {
    setIsClientModalOpen(false);
    // Update client options first
    setClientOptions((prev) => {
      const updated = prev.some((c) => c.id === client.id) ? prev : [...prev, client];
      return updated;
    });

    // Use setTimeout to ensure the state update has completed before setting form values
    setTimeout(() => {
      setValue('clientId', client.id, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setSelectedClientId(client.id);
      setValue('locationId', '');
      setValue('managerId', '');
    }, 0);
  };

  const handleLocationCreated = (location: Location) => {
    // Update location options first
    setLocationOptions((prev) => {
      const updated = prev.some((l) => l.id === location.id) ? prev : [...prev, location];
      return updated;
    });

    // Use setTimeout to ensure the state update has completed before setting form values
    setTimeout(() => {
      setValue('locationId', location.id, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }, 0);
  };

  const handleManagerCreated = (manager: Manager) => {
    setIsManagerModalOpen(false);
    // Update manager options first
    setManagerOptions((prev) => {
      const updated = prev.some((m) => m.id === manager.id) ? prev : [...prev, manager];
      return updated;
    });

    // Use setTimeout to ensure the state update has completed before setting form values
    setTimeout(() => {
      setValue('managerId', manager.id, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }, 0);
  };

  const onSubmit: SubmitHandler<JobFormInput> = async (data) => {
    try {
      if (isEditMode) {
        await updateJob(data);
        navigate(`/jobs/${id}`);
      } else {
        const newJob = await createJob(data);
        navigate(`/jobs/${newJob.id}`);
      }
    } catch (error) {
      console.error('Failed to save job:', error);
    }
  };

  if (jobLoading || clientsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const isPending = isCreating || isUpdating;

  return (
    <div>
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={isEditMode ? `/jobs/${id}` : '/jobs'}
            className="text-sm text-blue-600 hover:text-blue-800 mb-2 inline-block"
          >
            ← Back
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Job' : 'Create New Job'}
          </h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client <span className="text-red-600">*</span>
                  </label>
                  <Combobox
                    value={clientId}
                    onValueChange={(value: string) => {
                      setValue('clientId', value);
                      setValue('locationId', ''); // Reset location when client changes
                      setValue('managerId', ''); // Reset manager when client changes
                    }}
                    options={clientOptions.map((client) => ({
                      value: client.id,
                      label: formatClientName(client.name, client.firstName, client.lastName),
                    }))}
                    placeholder="Select a client"
                    searchPlaceholder="Search clients..."
                    emptyText="No clients found."
                  />
                  {errors.clientId && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.clientId.message}
                    </p>
                  )}
                  {!clientId && (
                    <button
                      type="button"
                      onClick={() => setIsClientModalOpen(true)}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline underline-offset-4"
                    >
                      + Add New Client
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manager
                  </label>
                  <Select
                    value={managerId || 'none'}
                    onValueChange={(value: string) => setValue('managerId', value === 'none' ? '' : value)}
                    disabled={!clientId || managersLoading}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !clientId
                            ? 'Select a client first'
                            : managersLoading
                            ? 'Loading managers...'
                            : 'Select a manager (optional)'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {managerOptions
                        .filter((manager) => manager.clientId === clientId)
                        .map((manager) => (
                          <SelectItem key={manager.id} value={manager.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{formatManagerName(manager.name, manager.firstName, manager.lastName)}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {clientId && (
                    <button
                      type="button"
                      onClick={() => setIsManagerModalOpen(true)}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline underline-offset-4"
                    >
                      + Add New Manager
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Address <span className="text-red-600">*</span>
                  </label>
                  <LocationCombobox
                    clientId={clientId}
                    value={locationId}
                    onValueChange={(value: string) => setValue('locationId', value)}
                    disabled={!clientId || locationsLoading}
                    locations={locationOptions.filter(
                      (location) => location.clientId === clientId
                    )}
                    placeholder={
                      !clientId
                        ? 'Select a client first'
                        : 'Select a service address'
                    }
                    defaultManagerId={managerId || undefined}
                    onLocationCreated={handleLocationCreated}
                  />
                  {errors.locationId && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.locationId.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Billing Address
                </label>
                <BillingAddressPicker
                  clientId={clientId}
                  managerId={managerId || undefined}
                  value={{
                    billingAddressLabel: watch('billingAddressLabel'),
                    billingAddressLine1: watch('billingAddressLine1'),
                    billingAddressLine2: watch('billingAddressLine2'),
                    billingCity: watch('billingCity'),
                    billingState: watch('billingState'),
                    billingZip: watch('billingZip'),
                  }}
                  onChange={(fields) => {
                    setValue('billingAddressLabel', fields.billingAddressLabel || '');
                    setValue('billingAddressLine1', fields.billingAddressLine1 || '');
                    setValue('billingAddressLine2', fields.billingAddressLine2 || '');
                    setValue('billingCity', fields.billingCity || '');
                    setValue('billingState', fields.billingState || '');
                    setValue('billingZip', fields.billingZip || '');
                  }}
                  disabled={!clientId}
                />
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Type <span className="text-red-600">*</span>
                  </label>
                  <Combobox
                    value={projectType}
                    onValueChange={(value: any) => {
                      setValue('projectType', value as any);
                      // Clear custom project type when switching away from "Other"
                      if (value !== 'Other') {
                        setValue('customProjectType', '');
                      }
                    }}
                    options={projectTypes.map((type) => ({
                      value: type,
                      label: type,
                    }))}
                    placeholder="Select a project type"
                    searchPlaceholder="Search project types..."
                    emptyText="No project types found."
                  />
                  {errors.projectType && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.projectType.message}
                    </p>
                  )}
                </div>

                {!isEditMode && candidates.length >= 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template
                    </label>
                    <Select
                      value={templateId ?? NO_TEMPLATE_VALUE}
                      onValueChange={(value: string) =>
                        setValue(
                          'templateId',
                          value === NO_TEMPLATE_VALUE ? null : value
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a template" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_TEMPLATE_VALUE}>
                          No template
                        </SelectItem>
                        {candidates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                            {t.isDefault && ' (default)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {projectType === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Service Type <span className="text-red-600">*</span>
                    </label>
                    <Input
                      {...register('customProjectType')}
                      placeholder="Enter custom service type..."
                    />
                    {errors.customProjectType && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.customProjectType.message}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add a description..."
                  />
                  {errors.description && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notice Date
                  </label>
                  <Input type="date" {...register('noticeDate')} />
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50 flex justify-end gap-3">
              <Link to={isEditMode ? `/jobs/${id}` : '/jobs'}>
                <Button type="button" variant="outline" disabled={isPending}>
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update Job'
                  : 'Create Job'}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <AddClientModal
        open={isClientModalOpen}
        onOpenChange={setIsClientModalOpen}
        onClientCreated={handleClientCreated}
      />

      {clientId && (
        <AddManagerModal
          open={isManagerModalOpen}
          onOpenChange={setIsManagerModalOpen}
          onManagerCreated={handleManagerCreated}
          clientId={clientId}
        />
      )}
    </div>
  );
}
